import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import ExportOutlined from '@ant-design/icons/ExportOutlined';
import FilterOutlined from '@ant-design/icons/FilterOutlined';
import MoreOutlined from '@ant-design/icons/MoreOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import SyncOutlined from '@ant-design/icons/SyncOutlined';
import { useQueryClient } from '@tanstack/react-query';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import Input, { type InputRef } from 'antd/es/input/Input';
import Modal from 'antd/es/modal';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import { type ColumnType } from 'antd/es/table';
import theme from 'antd/es/theme';
import Tooltip from 'antd/es/tooltip';
import uniqBy from 'lodash.uniqby';
import { useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { type Source, type SourcesQuery } from '@mai/types';

import ContentContainer from '../ContentContainer';
import Table from '../Table';
import TableHeader from '../TableHeader';

import { MODAL_WIDTH } from '@components/GlobalStyle';
import { updateSource, useSourcesQuery } from '@queries/sources';
import { renderDate } from '@utils/dates';
import { logger } from '@utils/logger';
import { useBasePath, useTeamId } from '@utils/router';

const SourcesTableHeader = ({
  dataSource,
  selectedRowKeys,
  setSelectedRowKeys,
  setIsSubmitting,
  pageSize,
  page,
  isLoading,
  allowActions = true,
}: {
  dataSource: Source[];
  selectedRowKeys: React.Key[];
  setIsSubmitting: (isSubmitting: boolean) => void;
  setSelectedRowKeys: (selectedRowKeys: React.Key[]) => void;
  pageSize: string;
  page: number;
  isLoading: boolean;
  allowActions?: boolean;
}) => {
  const queryClient = useQueryClient();
  const [_searchParams, setSearchParams] = useSearchParams();
  const { modal } = App.useApp();
  return (
    <TableHeader>
      <div
        style={{
          width: '100%',
        }}
      >
        {selectedRowKeys.length > 0 && allowActions && (
          <div
            style={{
              display: 'flex',
              gap: '0.5rem',
            }}
          >
            <Button
              disabled={selectedRowKeys.length === 0}
              size="small"
              danger
              onClick={() => {
                return modal.confirm({
                  width: MODAL_WIDTH,
                  maskClosable: true,
                  title: 'Delete Sources',
                  content: `Are you sure you want to delete ${selectedRowKeys.length} sources? This action is not reversible.`,
                  closable: true,
                  icon: null,
                  okButtonProps: {
                    size: 'small',
                    danger: true,
                  },
                  cancelButtonProps: {
                    size: 'small',
                  },
                  onOk: async () => {
                    setIsSubmitting(true);
                    setSelectedRowKeys([]);
                    setIsSubmitting(false);
                  },
                });
              }}
            >
              Delete Selected
            </Button>
            <Button
              size="small"
              onClick={() => {
                void (async () => {
                  try {
                    await Promise.all(
                      selectedRowKeys.map((sourceId) =>
                        updateSource(sourceId.toString(), {
                          shouldProcess: true,
                        }),
                      ),
                    );
                  } catch (error) {
                    logger.error(error);
                  }
                })();
              }}
            >
              Re-sync Selected
            </Button>
          </div>
        )}
      </div>
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
        }}
      >
        <Select
          options={[
            { label: '10', value: '10' },
            { label: '25', value: '25' },
            { label: '50', value: '50' },
            { label: '100', value: '100' },
          ]}
          size="small"
          value={pageSize}
          suffixIcon={<>/ Page</>}
          onChange={(value) => {
            setSearchParams((prev) => {
              prev.set('pageSize', value);
              return prev;
            });
          }}
          style={{
            width: '5rem',
          }}
        />
        <Tooltip title="Previous Page">
          <Button
            size="small"
            disabled={page === 1}
            icon={<ArrowLeftOutlined />}
            onClick={() => {
              setSearchParams((prev) => {
                prev.set('page', (page - 1).toString());
                return prev;
              });
            }}
          />
        </Tooltip>
        <Tooltip title="Next Page">
          <Button
            size="small"
            disabled={dataSource.length < parseInt(pageSize ?? '10', 10)}
            icon={<ArrowRightOutlined />}
            onClick={() => {
              setSearchParams((prev) => {
                prev.set('page', (page + 1).toString());
                return prev;
              });
            }}
          />
        </Tooltip>
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<SyncOutlined spin={isLoading} />}
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: ['sources'],
              });
            }}
          />
        </Tooltip>
      </div>
    </TableHeader>
  );
};

const SourcesTable = ({
  size,
  showHeader = true,
  projectIds: baseProjectIds,
  allowActions = true,
  sourceIds,
}: {
  size?: 'small' | 'middle' | 'large';
  showHeader?: boolean;
  projectIds?: string[];
  allowActions?: boolean;
  sourceIds?: string[];
}) => {
  const { token } = theme.useToken();
  const basePath = useBasePath();
  const teamId = useTeamId();
  const [sourceToDelete, setSourceToDelete] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchInput = useRef<InputRef>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const pageSize = searchParams.get('pageSize') ?? '50';
  const page = parseInt(searchParams.get('page') ?? '1', 10);
  const projectIds = useMemo(() => {
    const rawValue = searchParams.get('projectIds');
    if (!rawValue) return baseProjectIds;
    return uniqBy(
      [...(baseProjectIds ?? []), ...rawValue.split(',')],
      (id: string) => id,
    );
  }, [searchParams, baseProjectIds]);
  const queryClient = useQueryClient();

  const sourcesQuery = useSourcesQuery(
    {
      titleLike: searchParams.get('title') ?? undefined,
      pageSize: parseInt(pageSize ?? '10', 10),
      page,
      orderBy:
        (searchParams.get('orderBy') as SourcesQuery['orderBy']) ?? 'title',
      orderDirection:
        (searchParams.get(
          'orderDirection',
        ) as SourcesQuery['orderDirection']) ?? 'asc',
      sourceIds,
      projectIds,
    },
    true,
  );

  const onConfirmDelete = async () => {
    if (!sourceToDelete) return;
    setIsSubmitting(true);
    // TODO: Implement delete source
    setSourceToDelete(null);
    setIsSubmitting(false);
  };

  const columns: ColumnType<Source>[] = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      fixed: 'left',
      align: 'left',
      ellipsis: true,
      filterIcon: (filtered: boolean) => (
        <SearchOutlined
          style={{ color: filtered ? token.colorPrimary : undefined }}
        />
      ),
      filterDropdown: ({ clearFilters }) => {
        const handleReset = () => {
          if (clearFilters) clearFilters();
          setSearchParams((prev) => {
            prev.delete('title');
            return prev;
          });
        };

        const onSearch = (value?: string) => {
          setSearchParams((prev) => {
            if (value) {
              prev.set('title', value);
            } else {
              prev.delete('title');
            }
            return prev;
          });
        };

        return (
          <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
            <Input
              ref={searchInput}
              placeholder="Search title"
              onPressEnter={() => {
                onSearch(searchInput.current?.input?.value);
              }}
              style={{ marginBottom: 8, display: 'block' }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  onSearch(searchInput.current?.input?.value);
                }}
                icon={<FilterOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Filter
              </Button>
              <Button onClick={handleReset} size="small" style={{ width: 90 }}>
                Reset
              </Button>
            </Space>
          </div>
        );
      },
      render: (value: string, record: Source) => {
        return (
          <Link to={`${basePath}/library/sources/${record.sourceId}`}>
            {value || '-'}
          </Link>
        );
      },
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      render: (url) =>
        url ? (
          <a href={url} target="_blank">
            {new URL(url).hostname}
            <ExportOutlined style={{ marginLeft: 8 }} />
          </a>
        ) : (
          'N/A'
        ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 200,
      render: (createdAt) => renderDate(createdAt),
    },
    ...(allowActions
      ? ([
          {
            key: 'actions',
            align: 'right',
            fixed: 'right',
            width: 50,
            render: (_: unknown, record: Source) => {
              return (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: [
                      {
                        key: 'view',
                        label: (
                          <Link
                            to={`/team/${teamId}/sources/${record.sourceId}`}
                          >
                            View
                          </Link>
                        ),
                      },
                      {
                        key: 're-sync',
                        label: 'Re-sync',
                        icon: <SyncOutlined />,
                        onClick: () => {
                          void (async () => {
                            try {
                              await updateSource(record.sourceId, {
                                shouldProcess: true,
                              });
                              await queryClient.invalidateQueries({
                                queryKey: ['sources'],
                              });
                            } catch (error) {
                              logger.error(error);
                            }
                          })();
                        },
                      },
                      {
                        key: 'delete',
                        label: 'Delete',
                        danger: true,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                          setSourceToDelete(record.sourceId);
                        },
                      },
                    ],
                  }}
                  placement="bottomRight"
                >
                  <Button icon={<MoreOutlined />}></Button>
                </Dropdown>
              );
            },
          },
        ] as const)
      : []),
  ];

  const dataSource = sourcesQuery.data ?? [];

  if (sourcesQuery.isError) {
    return <ContentContainer.Error />;
  }

  return (
    <>
      <Modal
        title="Delete Source"
        open={sourceToDelete !== null}
        onOk={onConfirmDelete}
        okText="Delete"
        okType="danger"
        onCancel={() => setSourceToDelete(null)}
        okButtonProps={{ loading: isSubmitting }}
      >
        Are you sure you want to delete this source?
      </Modal>
      {showHeader && (
        <SourcesTableHeader
          dataSource={dataSource}
          selectedRowKeys={selectedRowKeys}
          setIsSubmitting={setIsSubmitting}
          setSelectedRowKeys={setSelectedRowKeys}
          pageSize={pageSize}
          page={page}
          isLoading={sourcesQuery.isLoading}
          allowActions={allowActions}
        />
      )}
      <Table
        columns={columns}
        loading={sourcesQuery.isLoading}
        dataSource={dataSource}
        size={size ?? 'middle'}
        rowKey="sourceId"
        pagination={false}
        rowSelection={
          allowActions
            ? {
                columnWidth: 40,
                type: 'checkbox',
                onChange: (selectedRowKeys) => {
                  setSelectedRowKeys(selectedRowKeys);
                },
              }
            : undefined
        }
      />
    </>
  );
};

export default SourcesTable;
